export type SlWorkspaceRole =
  | 'admin'
  | 'financeiro'
  | 'vendedor'
  | 'operacional';

export type SlWorkspaceSession = {
  schema: 'sltintas.sl-workspace.native-profile/v1';
  username: string;
  display_name: string;
  role: SlWorkspaceRole;
  subject: string;
};

export type SlLauncherItem = {
  id: string;
  label: string;
  description: string;
  href: string;
  roles: readonly SlWorkspaceRole[];
  visibleBeforeRoleSync?: boolean;
};

const ALL_WORKSPACE_ROLES: readonly SlWorkspaceRole[] = [
  'admin',
  'financeiro',
  'vendedor',
  'operacional',
];

/**
 * The SL fork profile is deliberately isolated in one module. Keeping product
 * copy and launcher links here makes upstream syncs smaller and keeps the
 * existing Portal available as a reversible launcher while native Workspace
 * identity remains the authorization boundary.
 */
export const SL_WORKSPACE_PROFILE = {
  enabled: true,
  productName: 'SL Workspace',
  organizationName: 'SL Distribuidora',
  locale: 'pt-BR',
  portalOrigin: 'https://sistema.sltintas.com.br',
  brand: {
    themeColor: '#061161',
    backgroundColor: '#F4F7FB',
    favicon: '/sl-workspace-favicon.ico',
    icon192: '/sl-workspace-icon-192.png',
    icon512: '/sl-workspace-icon-512.png',
    maskableIcon512: '/sl-workspace-maskable-512.png',
  },
  auth: {
    heading: 'Bem-vindo ao SL Workspace',
    description:
      'Entre com sua conta Google da SL Tintas ou Santa Maria Suprimentos.',
    ownerEmail: 'leonan@workspace.sltintas.com.br',
    googleAction: 'Entrar com Google',
    emailAction: 'Entrar com senha',
    managedAccountNotice:
      'Sua conta é criada e gerenciada pela SL Distribuidora.',
    loginAction: 'Entrar com sua conta',
    internalUseNotice: 'Uso interno da SL Distribuidora.',
  },
  legal: {
    upstreamName: 'Macro',
    upstreamRepository: 'https://github.com/macro-inc/macro',
    rootLicense: 'GNU Affero General Public License v3.0',
    licenseHref: '/legal/AGPL-3.0.txt',
    noticeHref: '/legal/SOURCE-NOTICE.txt',
    sourceHref: '/legal/sl-workspace-source.tar.gz',
    sourceManifestHref: '/legal/source-manifest.json',
  },
  capabilities: {
    externalAnalytics: false,
    emailSend: false,
    externalMcpConnect: false,
    githubPersonalOauth: false,
    githubAppInstall: false,
    googleLogin: true,
    appleLogin: false,
    billing: false,
    referralInvites: false,
    teamInvites: false,
    selfServiceAccountDeletion: false,
  },
  launcher: [
    {
      id: 'portal',
      label: 'Portal SL',
      description: 'Acesse os sistemas atuais da empresa.',
      href: 'https://sistema.sltintas.com.br/',
      roles: ALL_WORKSPACE_ROLES,
      visibleBeforeRoleSync: true,
    },
    {
      id: 'comercial',
      label: 'Comercial',
      description: 'Clientes, produtos, pedidos e vendas.',
      href: 'https://sistema.sltintas.com.br/#comercial/clientes',
      roles: ['admin', 'financeiro', 'vendedor'],
    },
    {
      id: 'producao',
      label: 'Producao',
      description: 'Ordens, formulas e acompanhamento operacional.',
      href: 'https://sistema.sltintas.com.br/#comercial/producao',
      roles: ['admin', 'financeiro', 'operacional'],
    },
    {
      id: 'financeiro',
      label: 'Financeiro',
      description: 'Caixa, faturamento, contas a receber e a pagar.',
      href: 'https://sistema.sltintas.com.br/#financeiro/overview',
      roles: ['admin', 'financeiro'],
    },
    {
      id: 'ppr',
      label: 'PPR',
      description: 'Checklists e indicadores autorizados.',
      href: 'https://sistema.sltintas.com.br/#ppr/checklists',
      roles: ['admin', 'operacional'],
    },
    {
      id: 'projetos',
      label: 'Projetos',
      description: 'Portfolio, compromissos e reuniao semanal.',
      href: 'https://sistema.sltintas.com.br/#projetos/portfolio',
      roles: ['admin'],
    },
    {
      id: 'deva',
      label: 'Deva Financeiro',
      description: 'Mesa financeira e apoio a decisao.',
      href: 'https://sistema.sltintas.com.br/#ai/pagamentos',
      roles: ['admin', 'financeiro'],
    },
  ] satisfies readonly SlLauncherItem[],
} as const;

export function launcherItemsForRole(
  role?: SlWorkspaceRole
): readonly SlLauncherItem[] {
  return SL_WORKSPACE_PROFILE.launcher.filter((item) =>
    role
      ? (item.roles as readonly SlWorkspaceRole[]).includes(role)
      : item.visibleBeforeRoleSync === true
  );
}

const SL_WORKSPACE_ROLES = new Set<SlWorkspaceRole>(ALL_WORKSPACE_ROLES);

/** Load the native managed profile; credentials stay in secure auth transport. */
export async function fetchSlWorkspaceSession(
  fetcher: typeof fetch = fetch
): Promise<SlWorkspaceSession> {
  const response = await fetcher('/sl/profile', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('SL Workspace session is unavailable');
  const value: unknown = await response.json();
  if (value === null || typeof value !== 'object') {
    throw new Error('SL Workspace session is invalid');
  }
  const session = value as Partial<SlWorkspaceSession>;
  if (
    session.schema !== 'sltintas.sl-workspace.native-profile/v1' ||
    typeof session.username !== 'string' ||
    typeof session.display_name !== 'string' ||
    typeof session.subject !== 'string' ||
    typeof session.role !== 'string' ||
    !SL_WORKSPACE_ROLES.has(session.role as SlWorkspaceRole) ||
    !session.subject.startsWith('workspace:') ||
    session.subject.length <= 'workspace:'.length
  ) {
    throw new Error('SL Workspace session is invalid');
  }
  return session as SlWorkspaceSession;
}

export function slGreeting(hour: number): string {
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

export function slProductTitle(detail?: string): string {
  return detail
    ? `${SL_WORKSPACE_PROFILE.productName} - ${detail}`
    : SL_WORKSPACE_PROFILE.productName;
}

export type SlWorkspaceCapability =
  keyof typeof SL_WORKSPACE_PROFILE.capabilities;

export type SlWorkspaceRuntimeMode = 'full' | 'public' | 'loading';

/**
 * Keeps the provider-free SL login surface separate from the authenticated app.
 * An unknown cookie-backed session must wait for verification: mounting either
 * the protected runtime or the public login tree would make an unproven auth
 * decision.
 */
export function resolveSlWorkspaceRuntimeMode(
  authenticated: boolean | undefined,
  profileEnabled = SL_WORKSPACE_PROFILE.enabled
): SlWorkspaceRuntimeMode {
  if (!profileEnabled || authenticated === true) return 'full';
  if (authenticated === false) return 'public';
  return 'loading';
}

export function slCapabilityEnabled(
  capability: SlWorkspaceCapability
): boolean {
  return (
    !SL_WORKSPACE_PROFILE.enabled ||
    SL_WORKSPACE_PROFILE.capabilities[capability]
  );
}
